import { useState, useEffect, useRef } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { DownloadIcon, PlusIcon } from '@heroicons/react/solid';
import {
  AdminSystemPermissions,
  SuperAdminSystemPermissions,
} from '@wajeez-web/core/types';
import { useNavigate } from 'react-router-dom';
import { convertMarkdownToHtml } from '@wajeez-web/core/utils';
import {
  useContentSelector,
  useContentDispatch,
  FlowsActions,
  ComponentsActions,
} from '@wajeez-web/stores/content';
import { DeleteItemAlert } from '@wajeez-web/components/ui';
import {
  CommonActions,
  AlertType,
  useCommonDispatch,
} from '@wajeez-web/stores/common';
import { Content, Server } from '@wajeez-web/core/api-base';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import _ from 'lodash';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';
import { CloneModal } from '../components/CloneModal';

const ComponentsPage = () => {
  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();

  const [isCloneComponentModalOpen, setIsCloneComponentModalOpen] =
    useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string>();
  const [
    isCloneComponentSaveAndEditSuccess,
    setIsCloneComponentSaveAndEditSuccess,
  ] = useState(false);

  const cloneComponent = useContentSelector(
    ({ components }) => components.cloneComponent
  );

  const {
    tablePreferances,
    setTablePreferences,
    getData: getDatatableData,
    isTableReferencesLoading,
  } = HandleTableData(
    'components',
    ComponentsActions.getComponentsDatatableAsync,
    undefined,
    undefined,
    [{ property: 1, orderByDirection: 1 }]
  );

  const dataTableData = useContentSelector(
    ({ components }) => components.componentsDatatable
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (isCloneComponentSaveAndEditSuccess && cloneComponent.data) {
      navigate(`/components/${cloneComponent.data}/edit`);
    }
  }, [isCloneComponentSaveAndEditSuccess, cloneComponent.data]);

  return (
    <>
      <PageLayoutsContainer
        title={`Components`}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add Component'}
                Icon={PlusIcon}
                onClick={() => {
                  navigate('/components/new');
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [SuperAdminSystemPermissions.components.add],
          },
        ]}
        breadCrumbsLinks={[{ title: '' }]}
      >
        <DataTable
          dataTableData={dataTableData.data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          renderRowActions={(row) => {
            return (
              <div className="w-full flex justify-center items-center">
                <Dropdown
                  id={`action-${row[1]}`}
                  items={[
                    {
                      label: 'Edit',
                      onClick: () => {
                        navigate(`/components/${row[1]}/edit`);
                      },
                    },
                    {
                      label: 'Clone',
                      onClick: () => {
                        setSelectedComponentId(row[1] as string);
                        setIsCloneComponentModalOpen(true);
                      },
                    },
                  ]}
                  label={
                    <div style={{ color: '#343a40' }}>
                      <DotsHorizontalIcon
                        style={{ width: '28px', height: '24px' }}
                      />
                    </div>
                  }
                  buttonStyle={{
                    width: '34px',
                    minWidth: '34px',
                    paddingRight: '2px',
                    paddingLeft: '2px',
                    color: 'black',
                  }}
                />
              </div>
            );
          }}
          isLoading={dataTableData.status === 'loading'}
        />
      </PageLayoutsContainer>
      {isCloneComponentModalOpen && (
        <CloneModal
          isOpen={isCloneComponentModalOpen}
          onClose={() => {
            setIsCloneComponentModalOpen(false);
          }}
          title="Clone Component"
          onSave={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneComponentModalOpen(false);

              tablePreferances && setTablePreferences({ ...tablePreferances });
            }
          }}
          onSaveAndEdit={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneComponentSaveAndEditSuccess(true);
            }
          }}
          isLoading={cloneComponent.status === 'loading'}
        />
      )}
    </>
  );
};

export default ComponentsPage;
